import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMeetingStore } from "@/stores/meetingStore";
import { MeetingCard } from "@/components/MeetingCard";
import { FolderChips } from "@/components/FolderChips";
import { supabase } from "@/lib/supabase";
import type { Meeting } from "@/types";

export function Dashboard() {
  const { t } = useTranslation();
  const { meetings, loading, fetchMeetings, deleteMeeting } =
    useMeetingStore();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Meeting[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [folderFilter, setFolderFilter] = useState<string | null | "unfiled">(null);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      const { data: rows, error: rpcError } = await supabase.rpc(
        "search_user_meetings",
        { search_query: q }
      );
      if (rpcError || !rows || rows.length === 0) {
        setSearchResults(rpcError ? null : []);
        setSearching(false);
        return;
      }
      const ids = rows.map((r: { meeting_id: string }) => r.meeting_id);
      const { data: meets } = await supabase
        .from("meetings")
        .select("*")
        .in("id", ids);
      const map = new Map((meets ?? []).map((m: Meeting) => [m.id, m]));
      const ordered = ids
        .map((id: string) => map.get(id))
        .filter(Boolean) as Meeting[];
      setSearchResults(ordered);
      setSearching(false);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  const baseList = searchResults ?? meetings;
  const list =
    folderFilter === null
      ? baseList
      : folderFilter === "unfiled"
      ? baseList.filter((m) => m.folder_id == null)
      : baseList.filter((m) => m.folder_id === folderFilter);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-redi-text">
          {t("meetings.title")}
        </h2>
        <button
          onClick={() => fetchMeetings()}
          className="text-sm text-redi-muted hover:text-redi-text"
        >
          {t("common.refresh")}
        </button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("meetings.searchPlaceholder")}
            className="w-full px-4 py-3 pr-10 bg-redi-surface border border-white/5 rounded-lg text-redi-text placeholder-redi-muted focus:outline-none focus:border-redi-primary"
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-redi-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        {searchResults != null && (
          <p className="text-redi-muted text-xs mt-2">
            {t("meetings.searchHint", { count: searchResults.length })}
          </p>
        )}
      </div>

      <FolderChips selected={folderFilter} onSelect={setFolderFilter} />

      {loading && meetings.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-redi-surface rounded-xl p-4 animate-pulse h-20"
            />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-redi-muted">
            {search ? t("meetings.noResults") : t("meetings.empty")}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((meeting) => (
            <MeetingCard
              key={meeting.id}
              meeting={meeting}
              onDelete={deleteMeeting}
            />
          ))}
        </div>
      )}
    </div>
  );
}
